import { useTranslation, Trans } from 'react-i18next';

import { Button, Skeleton } from '@ctct/payflow-ui';
import { Alert, Col, Row } from '@ctct/presentation-layer';

import { useSecurePay } from './useSecurePay';
import type { PaymentMethodsLoadedProps } from '../widgets/PaymentMethodsWidget/types';

interface Props {
  data: Pick<
    PaymentMethodsLoadedProps['data'],
    'getEcheckAccountType' | 'getEcheckFormattedAccountNumber' | 'getSecurePayEcheckURL' | 'getSecurePayIframeId'
  >;
  handlers: Pick<
    PaymentMethodsLoadedProps['handlers'],
    | 'onStartListeningSecurePay'
    | 'onStopListeningSecurePay'
    | 'onSecurePayEditClick'
    | 'onSecurePayIframeLoad'
    | 'onSecurePayIframeError'
    | 'onSecurePayCancelEditClick'
    | 'clearTimeoutIds'
  >;
  hooks: Pick<PaymentMethodsLoadedProps['hooks'], 'useSecurePayIframeState' | 'useTabsLoading' | 'useFullLoading'>;
}

export const ECheck = ({ data, handlers, hooks }: Props) => {
  const { t } = useTranslation();

  const { addedCssToIframe, showCvvTooltip, echeckConnected, securePayEdited, showSecurePayIframeBlockedMessage } =
    hooks.useSecurePayIframeState();

  const tabsLoading = hooks.useTabsLoading();
  const isFullLoading = hooks.useFullLoading();

  const isLoading = tabsLoading || isFullLoading;

  useSecurePay(handlers);

  if (echeckConnected && !securePayEdited) {
    return (
      <Row>
        <Col fixed>
          <span>
            <Trans
              i18nKey="payment-methods.echeck_existing"
              values={{
                accountType: data.getEcheckAccountType(),
                accountNumber: data.getEcheckFormattedAccountNumber(),
              }}
            />
          </span>
        </Col>
        <Col>
          {!isLoading && (
            <Button
              className="edit-echeck-button"
              data-qe-id="edit-echeck"
              variant="text"
              onClick={handlers.onSecurePayEditClick}
            >
              {t('payment-methods.edit')}
            </Button>
          )}
        </Col>
      </Row>
    );
  }

  if (showSecurePayIframeBlockedMessage) {
    return <Alert variant="warning" message={t('payment-methods.credit_card_loading_error')} />;
  }

  return (
    <>
      <Row style={{ position: 'relative' }}>
        <Col>
          {addedCssToIframe ? null : <Skeleton height={320} />}
          <iframe
            src={data.getSecurePayEcheckURL()}
            onLoad={handlers.onSecurePayIframeLoad}
            onError={handlers.onSecurePayIframeError}
            data-qe-id={data.getSecurePayIframeId()}
            id={data.getSecurePayIframeId()}
            className="echeck-iframe"
            title="echeck"
            loading="eager"
            style={
              // previously we used display: none; to hide iframe but it could cause issues with iframe loading, so now we use opacity and position to achieve the same effect
              addedCssToIframe
                ? {
                    opacity: 1,
                    position: 'static',
                    top: 'auto',
                    left: 'auto',
                  }
                : {
                    opacity: 0,
                    position: 'absolute',
                    top: '0',
                    left: '0',
                  }
            }
          />
        </Col>
        <Col fixed>
          {echeckConnected && (
            <Button
              variant="text"
              className="cancel-edit-echeck-button"
              data-qe-id="cancel-edit-echeck"
              onClick={handlers.onSecurePayCancelEditClick}
              busy={tabsLoading}
              disabled={tabsLoading}
            >
              {t('payment-methods.cancel')}
            </Button>
          )}
        </Col>
      </Row>
      {showCvvTooltip && (
        <div className="securepay-tooltip-container" data-qe-id="securepay-tooltip-container">
          <img
            alt={t('payment-methods.bank_info_alt')}
            className="securepay-tooltip"
            src="https://imgssl.constantcontact.com/ui/images1/papercheck.png"
          />
        </div>
      )}
    </>
  );
};
